import { useContext, useState, useRef, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import { DataContext } from '@aytm/app/stats/page/sidebar/coding/CodingPanel'

import { decodeText } from '@aytm/app/stats/lib/util/helpers'

import { SKIPPER_ACTIVE_HELP, SKIPPER_DISABLED_HELP } from '@aytm/app/stats/page/sidebar/components/lib/helps'
import { DEFAULT_SELECTED_ACTION, DEFAULT_SELECTED_CODEBOOK } from '@aytm/app/stats/page/sidebar/coding/lib/constants'
import { nextProgress } from '@aytm/app/stats/page/sidebar/coding/lib/progress'

import { DEFAULT_SELECTED_QUESTION } from '@aytm/app/stats/page/comments/lib/constants'
import { getQuestionsList } from '@aytm/app/stats/page/comments/lib/utils'

import { generateSurveySkipper } from '@aytm/app/stats/page/comments/actions'

import { itemTemplate, valueTemplate, optionGroupTemplate } from '@aytm/app/stats/page/components/lib/templates'

import useEventListener from '@aytm/app/stats/page/components/hooks/useEventListener'

import Popup, { PopupCloseButton } from '@aytm/lib/rfd-ui/components/basic/popup'

import { Button } from 'primereact/button'
import { Dropdown } from 'primereact/dropdown'

import SidebarPopup from '@aytm/app/stats/page/sidebar/components/SidebarPopup'

import style from '@aytm/app/stats/page/sidebar/coding/styles/CodingPanel.module.scss'

const SKIPPER_RUN_LABELS = {
  skipper: 'Run autocode',
  'skipper-apply': 'Apply codebook',
  'skipper-guided': 'Run guided autocode'
}

const CodingSkipper = ({
  survey,
  isProcessing,
  isSkipperProcessing,
  setIsProcessing,
  setIsSkipperProcessing,
  onFetch,
  onFetchApiKey
}) => {
  const { root, onCollapse, onCloseDropdown, onCancelEditing } = useContext(DataContext)

  const { questions, codeBooks = [] } = survey

  const [selectedQuestion, setSelectedQuestion] = useState(DEFAULT_SELECTED_QUESTION)
  const [selectedAction, setSelectedAction] = useState(DEFAULT_SELECTED_ACTION)
  const [selectedCodebook, setSelectedCodebook] = useState(DEFAULT_SELECTED_CODEBOOK)

  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState(null)

  const [progress, setProgress] = useState(0)

  const selectedQuestionRef = useRef()
  selectedQuestionRef.current = selectedQuestion

  const selectedActionRef = useRef()
  selectedActionRef.current = selectedAction

  const selectedCodebookRef = useRef()
  selectedCodebookRef.current = selectedCodebook

  const progressRef = useRef()
  progressRef.current = progress

  const skipperTimerRef = useRef(null)

  const questionsOptions = useMemo(
    () => getQuestionsList(survey),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [questions]
  )

  const actionsOptions = useMemo(() => {
    if (selectedQuestion == DEFAULT_SELECTED_QUESTION) return []

    const options = [{ text: 'Autocode', value: 'skipper' }]

    const codebooks = JSON.parse(JSON.stringify(codeBooks || []))
    if (0 < codebooks.length) {
      if (codebooks.some(codebook => 0 < codebook.tags.length))
        options.push({ text: 'Apply codebook', value: 'skipper-apply' })

      options.push({ text: 'Guided autocode', value: 'skipper-guided' })
    }

    return options
  }, [codeBooks, selectedQuestion])

  const codebooksOptions = useMemo(() => {
    if (selectedAction == DEFAULT_SELECTED_ACTION) return []

    const codebooks = JSON.parse(JSON.stringify(codeBooks || []))
    codebooks.sort((a, b) => a.ts - b.ts)

    return codebooks.map(codebook => {
      return {
        type: codebook.type,
        text: codebook.title,
        search: decodeText(codebook.title),
        value: codebook.title
      }
    })
  }, [codeBooks, selectedAction])

  const isSkipperDisabled = useMemo(
    () =>
      !selectedQuestion ||
      selectedQuestion == DEFAULT_SELECTED_QUESTION ||
      (selectedAction != DEFAULT_SELECTED_ACTION && selectedCodebook == DEFAULT_SELECTED_CODEBOOK),
    [selectedQuestion, selectedAction, selectedCodebook]
  )

  const onQuestionSelect = event => setSelectedQuestion(event.value)

  const onActionSelect = useCallback(
    event => {
      setSelectedAction(event.value)

      if ('skipper-apply' == event.value && selectedCodebookRef.current != DEFAULT_SELECTED_CODEBOOK) {
        const codebook = codeBooks.find(codebook => codebook.title == selectedCodebookRef.current)
        if (!codebook.tags.length) {
          setSelectedCodebook(codeBooks.find(codebook => 0 < codebook.tags.length).title)
        }
      }
    },
    [codeBooks]
  )

  const onCodebookSelect = useCallback(
    event => {
      setSelectedCodebook(event.value)

      if ('skipper-apply' == selectedActionRef.current) {
        const codebook = codeBooks.find(codebook => codebook.title == event.value)
        if (!codebook.tags.length) setSelectedAction('skipper-guided')
      }
    },
    [codeBooks]
  )

  const onMessage = useCallback((message = [], messageType = null) => {
    setMessage((1 < message.length ? message.map((v, i) => (0 < i ? v : `<b>${v}</b>`)) : message).join('<br/>'))
    setMessageType(messageType)
  }, [])

  const onProgress = useCallback(value => setProgress(nextProgress(progressRef.current, value)), [])

  const onSkipperConfirm = useCallback(async () => {
    const params = {
      selectedQuestion: selectedQuestionRef.current,
      onFetch: (data = {}, callback) => {
        if (Object.hasOwn(data, 'codebook')) {
          onCollapse(data.codebook.title, false)
        }

        onFetch(data, callback)
      },
      onError: message => onMessage(message, 'error'),
      onProgress,
      setIsLoading: (value, message) => {
        if (!value) {
          onMessage(message)
          onProgress(value)
        }

        setIsProcessing(value)
        setIsSkipperProcessing(value)
      },
      onFetchApiKey
    }

    if ('skipper-guided' == selectedActionRef.current) {
      params.guided = true
    }

    const codebook = codeBooks.find(codebook => codebook.title == selectedCodebookRef.current)
    if (codebook != undefined) {
      params.codebook = {
        codebook: codebook.title,
        type: codebook.type,
        tags: codebook.tags
      }
    }

    onMessage()

    onCloseDropdown()
    onCancelEditing()

    await generateSurveySkipper(params)
  }, [
    codeBooks,
    setIsProcessing,
    setIsSkipperProcessing,
    onMessage,
    onProgress,
    onCollapse,
    onFetch,
    onFetchApiKey,
    onCloseDropdown,
    onCancelEditing
  ])

  useEffect(() => {
    onMessage()

    const _questionsOptions = questionsOptions.flatMap(group => group.items).filter(o => !o.disabled)
    if (0 < _questionsOptions.length) {
      if (
        selectedQuestionRef.current == DEFAULT_SELECTED_QUESTION ||
        !_questionsOptions.some(question => question.value == selectedQuestionRef.current)
      ) {
        setSelectedQuestion(_questionsOptions[0].value)
      }
    } else {
      setSelectedQuestion(DEFAULT_SELECTED_QUESTION)
    }
  }, [questionsOptions, onMessage])

  useEffect(() => {
    onMessage()

    if (0 < actionsOptions.length) {
      if (
        selectedActionRef.current == DEFAULT_SELECTED_ACTION ||
        !actionsOptions.some(action => action.value == selectedActionRef.current)
      ) {
        setSelectedAction(actionsOptions[0].value)
      }
    } else {
      setSelectedAction(DEFAULT_SELECTED_ACTION)
    }
  }, [actionsOptions, onMessage])

  useEffect(() => {
    onMessage()

    if (0 < codebooksOptions.length) {
      if (
        selectedCodebookRef.current == DEFAULT_SELECTED_CODEBOOK ||
        !codebooksOptions.some(codebook => codebook.value == selectedCodebookRef.current)
      ) {
        setSelectedCodebook(
          'skipper-apply' == selectedActionRef.current
            ? codeBooks.find(codebook => 0 < codebook.tags.length)?.title || codebooksOptions[0].value
            : codebooksOptions[0].value
        )
      }
    } else {
      setSelectedCodebook(DEFAULT_SELECTED_CODEBOOK)
    }
  }, [codeBooks, codebooksOptions, onMessage])

  useEffect(
    () => () => {
      if (skipperTimerRef.current) clearTimeout(skipperTimerRef.current)
    },
    []
  )

  useEventListener('assignCodingSkipper', event => {
    if (event.detail) {
      const { question, action, codebook } = event.detail

      if (question != selectedQuestionRef.current) setSelectedQuestion(question)
      if (action != selectedActionRef.current) setSelectedAction(action)
      if (codebook.codebook != selectedCodebookRef.current) setSelectedCodebook(codebook.codebook)
    }
  })

  useEventListener('runCodingSkipper', event => {
    if (event.detail) {
      const { question, codebook } = event.detail
      const action = 0 < codebook.tags.length ? 'skipper-apply' : 'skipper-guided'

      if (question != selectedQuestionRef.current) {
        selectedQuestionRef.current = question
        setSelectedQuestion(question)
      }
      if (action != selectedActionRef.current) {
        selectedActionRef.current = action
        setSelectedAction(action)
      }
      if (codebook.codebook != selectedCodebookRef.current) {
        selectedCodebookRef.current = codebook.codebook
        setSelectedCodebook(codebook.codebook)
      }

      if (skipperTimerRef.current) clearTimeout(skipperTimerRef.current)
      skipperTimerRef.current = setTimeout(() => onSkipperConfirm(), 200)
    }
  })

  return (
    <>
      <div className={style['component-title']}>Skipper autocoding</div>

      <div className={style['component-content']}>
        <div className={style.row}>
          <div className="p-fluid p-col-12">
            <Dropdown
              data-testid="stat-coding-skipper-question-dropdown"
              className="p-dropdown-rounded"
              placeholder="Select question..."
              value={selectedQuestion}
              options={questionsOptions}
              optionValue="value"
              optionLabel="text"
              optionDisabled="disabled"
              optionGroupLabel="text"
              optionGroupChildren="items"
              optionGroupTemplate={optionGroupTemplate}
              itemTemplate={itemTemplate}
              valueTemplate={valueTemplate}
              filter
              filterBy="search,text"
              filterPlaceholder="Search items"
              resetFilterOnHide
              filterInputAutoFocus={false}
              fixedDropdownDir="bottom"
              disabled={isProcessing}
              onChange={onQuestionSelect}
            />
          </div>
        </div>

        {0 < actionsOptions.length ? (
          <div className={style.row}>
            <div className="p-fluid p-col-12">
              <Dropdown
                data-testid="stat-coding-skipper-action-dropdown"
                className="p-dropdown-rounded"
                aria-label="Autocoding action"
                ariaLabel="Autocoding action"
                value={selectedAction}
                options={actionsOptions}
                optionValue="value"
                optionLabel="text"
                fixedDropdownDir="bottom"
                disabled={isProcessing}
                itemTemplate={itemTemplate}
                valueTemplate={valueTemplate}
                onChange={onActionSelect}
              />
            </div>
          </div>
        ) : null}

        {0 < codebooksOptions.length ? (
          <div className={style.row}>
            <div className="p-fluid p-col-12">
              <Dropdown
                data-testid="stat-coding-skipper-codebook-dropdown"
                className="p-dropdown-rounded"
                aria-label="Codebook"
                ariaLabel="Codebook"
                value={selectedCodebook}
                options={codebooksOptions}
                optionValue="value"
                optionLabel="text"
                filterBy="search,text"
                filterPlaceholder="Search items"
                fixedDropdownDir="bottom"
                filter
                resetFilterOnHide
                filterInputAutoFocus={false}
                disabled={isProcessing}
                itemTemplate={itemTemplate}
                valueTemplate={valueTemplate}
                onChange={onCodebookSelect}
              />
            </div>
          </div>
        ) : null}

        <div className={`${style.row} ${style['row-skipper']}`}>
          {0 < message.length ? (
            <Popup
              className={style['component-popup']}
              width="250px"
              trigger="none"
              direction="bottomleft"
              popupOffset={9}
              show
              hideOtherPopupsOnShow={false}
              content={() => (
                <>
                  <PopupCloseButton className={style['component-popup-close']} onClose={onMessage}>
                    <i data-testid="stat-coding-skipper-message-close" className="icon-aytmfd-x-v2" />
                  </PopupCloseButton>
                  <div
                    className={classNames(style['component-popup-content'], {
                      [style['component-popup-content-failed']]: 'error' == messageType,
                      [style['component-popup-content-success']]: 'error' != messageType
                    })}
                  >
                    {'error' == messageType ? (
                      <span className={style['component-popup-icon']}>🧭</span>
                    ) : (
                      <span className={style['component-popup-icon']}>🙌</span>
                    )}
                    <span dangerouslySetInnerHTML={{ __html: message }}></span>
                  </div>
                </>
              )}
            >
              <div className={`${style['component-popup-anchor']} rfd-ui-popup-target-anchor`} />
            </Popup>
          ) : null}

          {isSkipperProcessing ? (
            <div className={style.skipper}>
              <span className={style['skipper-fill']} style={{ width: `${progress}%` }}></span>
              <i className={`icon-aytmfd-skipper ${style['skipper-processing']}`} />
              <span className={style['skipper-text']}>{SKIPPER_RUN_LABELS[selectedAction]}</span>
            </div>
          ) : (
            <Button
              data-testid="stat-coding-skipper-run-button"
              className="stat-skipper-tags-button -dtr-stat-skipper-tags-button p-button-rounded p-button-blue"
              label={SKIPPER_RUN_LABELS[selectedAction]}
              icon={<i className="icon-aytmfd-skipper" />}
              iconPos="left"
              disabled={isProcessing || isSkipperDisabled}
              onClick={isProcessing || isSkipperDisabled ? null : onSkipperConfirm}
            />
          )}

          {root && (
            <SidebarPopup
              id="tags-skipper"
              root={root}
              help={isSkipperDisabled ? SKIPPER_DISABLED_HELP : SKIPPER_ACTIVE_HELP}
            />
          )}
        </div>
      </div>
    </>
  )
}

CodingSkipper.propTypes = {
  survey: PropTypes.object,
  isProcessing: PropTypes.bool,
  isSkipperProcessing: PropTypes.bool,
  setIsProcessing: PropTypes.func,
  setIsSkipperProcessing: PropTypes.func,
  onFetch: PropTypes.func,
  onFetchApiKey: PropTypes.func
}

CodingSkipper.displayName = 'CodingSkipper'

export default CodingSkipper
